import {
  useState,
  useCallback,
  createContext,
  useContext,
  ReactNode,
  useRef,
} from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface ConfirmOptions {
  title?: ReactNode;
  description?: ReactNode;
  content?: ReactNode;
  confirmationText?: string;
  cancellationText?: string;
  destructiveText?: string;
  titleProps?: React.HTMLAttributes<HTMLDivElement>;
}

interface ConfirmContextType {
  confirm: (opts: ConfirmOptions) => Promise<{ confirmed: boolean }>;
}

const ConfirmContext = createContext<ConfirmContextType | null>(null);

export const ConfirmProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: { confirmed: boolean }) => void) | null
  >(null);
  const wasConfirmed = useRef(false);

  const confirm = useCallback(
    (opts: ConfirmOptions): Promise<{ confirmed: boolean }> => {
      return new Promise((resolve) => {
        setOptions(opts);
        setOpen(true);
        wasConfirmed.current = false;
        setResolvePromise(() => resolve);
      });
    },
    [],
  );

  const handleConfirm = useCallback(() => {
    wasConfirmed.current = true;
  }, []);

  const handleCancel = useCallback(() => {
    wasConfirmed.current = false;
  }, []);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        if (resolvePromise) {
          resolvePromise({ confirmed: wasConfirmed.current });
          setResolvePromise(null);
        }
        wasConfirmed.current = false;
      }
    },
    [resolvePromise],
  );

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            {options?.title && (
              <AlertDialogTitle {...options.titleProps}>
                {options.title}
              </AlertDialogTitle>
            )}
          </AlertDialogHeader>
          {(options?.content || options?.description) && (
            <AlertDialogDescription asChild>
              <div>{options.content || options.description}</div>
            </AlertDialogDescription>
          )}
          <AlertDialogFooter>
            {options?.cancellationText && (
              <AlertDialogCancel asChild onClick={handleCancel}>
                <Button variant="outline">{options?.cancellationText}</Button>
              </AlertDialogCancel>
            )}
            {options?.confirmationText && (
              <AlertDialogAction asChild onClick={handleConfirm}>
                <Button variant="default">{options?.confirmationText}</Button>
              </AlertDialogAction>
            )}
            {options?.destructiveText && (
              <AlertDialogAction
                variant="destructive"
                asChild
                onClick={handleConfirm}
              >
                <Button variant="destructive">
                  {options?.destructiveText}
                </Button>
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error("useConfirm must be used within a ConfirmProvider");
  }
  return context;
};
